/**
 * Utility functions for objectives/objetivos feature
 */

/**
 * Returns true if at least one objetivo is associated with the given platform.
 * Platform values come from `plataformas_relacionadas` and are typically 'GOOGLE' / 'META'.
 *
 * @param {Array} objetivos
 * @param {string} platform - 'GOOGLE' | 'META'
 * @returns {boolean}
 */
export const hasPlatformObjetivos = (objetivos, platform) => {
  if (!Array.isArray(objetivos) || !platform) return false;
  return objetivos.some(
    (o) => Array.isArray(o?.plataformas_relacionadas) && o.plataformas_relacionadas.includes(platform)
  );
};

/**
 * Desviación vs target: texto y color para mostrar "X% debajo/sobre el objetivo" o "En objetivo".
 * CPA/Presupuesto: menor es mejor. ROAS/Conversiones: mayor es mejor.
 * @param {Object} objetivo - { valor_actual, target, metrica_relacionada }
 * @returns {{ text: string, color: string } | null}
 */
export const getDesviacionText = (objetivo) => {
  const actual = objetivo.valor_actual;
  const target = objetivo.target;
  const metrica = objetivo.metrica_relacionada;
  if (target == null || target === 0) return null;
  const a = Number(actual);
  const t = Number(target);
  if (!Number.isFinite(a) || !Number.isFinite(t)) return null;
  const isLowerBetter = metrica === 'Presupuesto' || metrica === 'CPA';
  const pct = ((a - t) / t) * 100;
  if (Math.abs(pct) < 5) return { text: 'En objetivo', color: 'text-green-400' };
  const abs = Math.round(Math.abs(pct));
  if (pct < 0) {
    if (isLowerBetter) return { text: `${abs}% debajo del objetivo`, color: 'text-green-400' };
    return { text: `${abs}% debajo del objetivo`, color: 'text-amber-400' };
  }
  if (isLowerBetter) return { text: `${abs}% sobre el objetivo`, color: 'text-amber-400' };
  return { text: `${abs}% sobre el objetivo`, color: 'text-green-400' };
};

/**
 * Determina si un objetivo está "en riesgo" según la métrica y valor actual vs target.
 * - CPA: en riesgo si actual > 115% del target; por debajo del 100% es excelente.
 * - ROAS: en riesgo si actual < 95% del target; >= 95% es on target.
 * - Conversiones: en riesgo si valor PROYECTADO < 90% del target (usa valor_proyectado; fallback a valor_actual).
 * Presupuesto no se evalúa aquí (tiene su propia sección).
 * @param {Object} objetivo - { valor_actual, valor_proyectado, target, metrica_relacionada }
 * @returns {boolean}
 */
export const isObjetivoEnRiesgo = (objetivo) => {
  const actual = Number(objetivo.valor_actual);
  const proyectado = Number(objetivo.valor_proyectado);
  const target = Number(objetivo.target);
  const metrica = String(objetivo.metrica_relacionada || '').toUpperCase();
  if (!Number.isFinite(target) || target === 0) return false;

  if (metrica.includes('CPA')) {
    if (!Number.isFinite(actual)) return false;
    return actual > target * 1.15; // Por encima del 115% del target = en riesgo
  }
  if (metrica.includes('ROAS')) {
    if (!Number.isFinite(actual)) return false;
    return actual < target * 0.95; // Por debajo del 95% del target = en riesgo
  }
  if (metrica.includes('CONVERSION')) {
    // Para Conversiones se usa el valor PROYECTADO para definir si está en riesgo
    const valorParaRiesgo = Number.isFinite(proyectado) ? proyectado : actual;
    if (!Number.isFinite(valorParaRiesgo)) return false;
    return valorParaRiesgo < target * 0.9; // Por debajo del 90% = en riesgo
  }
  return false;
};

/**
 * Calculate progress percentage from actual value vs target
 * @param {number} actual - Current actual value
 * @param {number} target - Target value
 * @returns {number} Progress percentage (0 if target is invalid)
 */
export const calcularProgreso = (actual, target) => {
  if (!target || target === 0) return 0;
  return (actual / target) * 100;
};

/**
 * Get projection styling based on projected value vs target
 * Returns color and gradient classes for displaying projection status
 * 
 * For 'Presupuesto' and 'CPA' metrics: lower is better (green when <= 100%)
 * For other metrics: higher is better (green when >= 100%)
 * 
 * @param {number} proyectado - Projected value
 * @param {number} target - Target value
 * @param {string} metrica - Metric type ('Presupuesto', 'CPA', 'ROAS', 'Conversiones', etc.)
 * @returns {{ color: string, bgGradient: string }} Tailwind CSS classes for styling
 */
export const obtenerEstilosProyeccion = (proyectado, target, metrica) => {
  if (!proyectado || !target) return { 
    color: 'text-white/60', 
    bgGradient: 'bg-white/10'
  };
  
  const porcentaje = (proyectado / target) * 100;
  
  // For budget and CPA metrics, lower is better
  if (metrica === 'Presupuesto' || metrica === 'CPA') {
    if (porcentaje <= 100) {
      return { 
        color: 'text-green-300', 
        bgGradient: 'bg-gradient-to-r from-green-500 to-green-600'
      };
    } else if (porcentaje <= 110) {
      return { 
        color: 'text-yellow-300', 
        bgGradient: 'bg-gradient-to-r from-yellow-500 to-yellow-600'
      };
    } else {
      return { 
        color: 'text-red-300', 
        bgGradient: 'bg-gradient-to-r from-red-500 to-red-600'
      };
    }
  } else {
    // For other metrics (ROAS, Conversiones), higher is better
    if (porcentaje >= 100) {
      return { 
        color: 'text-green-300', 
        bgGradient: 'bg-gradient-to-r from-green-500 to-green-600'
      };
    } else if (porcentaje >= 90) {
      return { 
        color: 'text-yellow-300', 
        bgGradient: 'bg-gradient-to-r from-yellow-500 to-yellow-600'
      };
    } else {
      return { 
        color: 'text-red-300', 
        bgGradient: 'bg-gradient-to-r from-red-500 to-red-600'
      };
    }
  }
};

/**
 * Compute suggested daily budget and message for a Presupuesto (budget) objective.
 * Uses period from fecha_inicio/fecha_fin and current spend (valor_actual) vs target.
 *
 * @param {Object} objetivo - Objective with target, valor_actual, valor_proyectado, fecha_inicio, fecha_fin, currency
 * @returns {{ suggestedDaily: number|null, message: string, status: 'over'|'under'|'on_track', daysLeft: number, daysPassed: number }|null}
 */
export const getPresupuestoSugerido = (objetivo) => {
  if (!objetivo || objetivo.metrica_relacionada !== 'Presupuesto') return null;

  const target = parseFloat(objetivo.target);
  const actual = parseFloat(objetivo.valor_actual);
  const proyectado = objetivo.valor_proyectado != null ? parseFloat(objetivo.valor_proyectado) : null;

  if (Number.isNaN(target) || target <= 0) return null;

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  let fechaInicio = null;
  let fechaFin = null;

  if (objetivo.fecha_inicio) {
    const parts = String(objetivo.fecha_inicio).split('T')[0].split('-');
    if (parts.length >= 3) {
      fechaInicio = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      fechaInicio.setHours(0, 0, 0, 0);
    }
  }
  if (objetivo.fecha_fin) {
    const parts = String(objetivo.fecha_fin).split('T')[0].split('-');
    if (parts.length >= 3) {
      fechaFin = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      fechaFin.setHours(23, 59, 59, 999);
    }
  }

  if (!fechaInicio || !fechaFin || fechaInicio.getTime() > fechaFin.getTime()) return null;

  const daysInPeriod = Math.max(1, Math.ceil((fechaFin - fechaInicio) / (1000 * 60 * 60 * 24)));
  const daysPassed = Math.max(0, Math.min(daysInPeriod, Math.ceil((hoy - fechaInicio) / (1000 * 60 * 60 * 24))));
  const daysLeft = Math.max(0, daysInPeriod - daysPassed);

  if (daysLeft === 0) return null;

  const suggestedDaily = (target - (Number.isNaN(actual) ? 0 : actual)) / daysLeft;
  const projectedValue = proyectado != null && !Number.isNaN(proyectado)
    ? proyectado
    : (daysPassed > 0 && actual != null && !Number.isNaN(actual))
      ? (actual / daysPassed) * daysInPeriod
      : null;

  let status = 'on_track';
  if (projectedValue != null && !Number.isNaN(projectedValue)) {
    if (projectedValue > target) status = 'over';
    else if (projectedValue < target * 0.95) status = 'under';
  }

  return {
    suggestedDaily: Number.isFinite(suggestedDaily) ? suggestedDaily : null,
    status,
    daysLeft,
    daysPassed,
  };
};

